/* Fills the shell's main column rather than guessing a viewport offset —
   .main is a flex column, so flex:1 + min-height:0 makes the canvas the
   scrolling region instead of the page. */
.page {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: var(--bg-base);
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

/* ── Header owns the artifact ────────────────────────────── */

.header {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--border-subtle);
  flex-shrink: 0;
}

.backBtn,
.iconBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  background: none;
  border: none;
  border-radius: var(--radius-md);
  color: var(--text-muted);
  cursor: pointer;
  transition:
    color var(--duration-fast) var(--ease-default),
    background var(--duration-fast) var(--ease-default);
}

.backBtn:hover,
.iconBtn:hover:not(:disabled) {
  color: var(--text-primary);
  background: var(--bg-hover);
}

.iconBtn:disabled {
  opacity: 0.3;
  cursor: default;
}

/* Title doubles as the rename affordance — no settings dialog */
.name {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-2);
  background: none;
  border: none;
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--text-lg);
  font-weight: var(--weight-bold);
  color: var(--text-primary);
  cursor: text;
  min-width: 0;
}

.name:hover {
  background: var(--bg-hover);
}

.nameSubject {
  font-weight: var(--weight-regular);
  color: var(--text-muted);
}

.nameIcon {
  color: var(--text-disabled);
  flex-shrink: 0;
}

.nameInput {
  flex: 1;
  min-width: 0;
  max-width: 420px;
  height: 32px;
  padding: 0 var(--space-2);
  background: var(--bg-card);
  border: 1px solid var(--accent-secondary);
  border-radius: var(--radius-md);
  outline: none;
  font-family: var(--font-sans);
  font-size: var(--text-lg);
  font-weight: var(--weight-bold);
  color: var(--text-primary);
}

.headerActions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-left: auto;
}

.viewToggle {
  display: inline-flex;
  padding: 2px;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
}

.viewBtn {
  height: 26px;
  padding: 0 var(--space-3);
  background: none;
  border: none;
  border-radius: var(--radius-sm);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: var(--weight-semi);
  color: var(--text-muted);
  cursor: pointer;
}

.viewBtnActive {
  background: var(--bg-active);
  color: var(--text-primary);
}

.saveBtn {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: 30px;
  padding: 0 var(--space-3);
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: var(--weight-semi);
  color: var(--text-secondary);
  cursor: pointer;
}

.saveBtn:hover {
  color: var(--text-primary);
  border-color: var(--border-strong);
}

/* ── Split: chat ~32%, canvas dominant ───────────────────── */

.split {
  display: grid;
  grid-template-columns: minmax(300px, 32%) minmax(0, 1fr);
  flex: 1;
  min-height: 0;
}

.chatPanel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 1px solid var(--border-subtle);
  padding-right: var(--space-4);
}

.transcript {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-4) 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.chatEmpty {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4);
  border: 1px dashed var(--border-default);
  border-radius: var(--radius-lg);
}

.chatEmptyIcon {
  color: var(--accent-secondary-text);
}

.chatEmptyText {
  margin: 0;
  font-size: var(--text-base);
  color: var(--text-muted);
  line-height: var(--leading-relaxed);
}

.bubble {
  padding: var(--space-3);
  border-radius: var(--radius-lg);
  font-size: var(--text-base);
  line-height: var(--leading-relaxed);
  max-width: 92%;
}

.bubbleUser {
  align-self: flex-end;
  background: var(--bg-active);
  color: var(--text-primary);
}

.bubbleAgent {
  align-self: flex-start;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  color: var(--text-secondary);
}

.bubbleError {
  border-color: var(--color-danger);
  background: var(--color-danger-muted);
  color: var(--text-primary);
}

/* ── Pending turn ─────────────────────────────────────────── */

.typing {
  display: flex;
  align-items: center;
  gap: 5px;
  width: fit-content;
}

.dot {
  width: 6px;
  height: 6px;
  border-radius: var(--radius-pill);
  background: var(--text-muted);
  animation: blink 1.4s var(--ease-default) infinite both;
}

.dot:nth-child(2) {
  animation-delay: 0.2s;
}

.dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes blink {
  0%,
  80%,
  100% {
    opacity: 0.25;
  }
  40% {
    opacity: 1;
  }
}

/* Motion here is decorative — it conveys nothing the text doesn't. */
@media (prefers-reduced-motion: reduce) {
  .dot {
    animation: none;
    opacity: 0.6;
  }
}

.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* ── Suggestions in the empty transcript ──────────────────── */

.chatSuggestions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-2);
  margin-top: var(--space-3);
}

.chatSuggestion {
  padding: 5px var(--space-3);
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-pill);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--text-secondary);
  text-align: left;
  cursor: pointer;
  transition:
    color var(--duration-fast) var(--ease-default),
    border-color var(--duration-fast) var(--ease-default);
}

.chatSuggestion:hover {
  color: var(--text-primary);
  border-color: var(--ai-accent);
}

.chatSuggestion:focus-visible {
  outline: 2px solid var(--ai-accent);
  outline-offset: 2px;
}

.composer {
  display: flex;
  align-items: flex-end;
  gap: var(--space-2);
  padding: var(--space-3);
  margin-bottom: var(--space-4);
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  flex-shrink: 0;
}

/* The Plexie accent, matching the home composer — both are the same control,
   and one glowing yellow while the other glows periwinkle reads as a bug. */
.composer:focus-within {
  border-color: var(--ai-accent);
}

.composerInput {
  flex: 1;
  min-width: 0;
  /* Height is driven by content in JS, capped at 140px. */
  max-height: 140px;
  overflow-y: auto;
  background: none;
  border: none;
  outline: none;
  resize: none;
  font-family: var(--font-sans);
  font-size: var(--text-base);
  color: var(--text-primary);
  line-height: var(--leading-normal);
}

/* Same reason as the home composer: the wrapper draws the focus ring, and
   global.css's bare `:focus-visible` would otherwise paint a second, yellow
   one on top of it. Matching the state here wins on specificity. */
.composerInput:focus-visible {
  outline: none;
}

.composerInput::placeholder {
  color: var(--text-muted);
}

.composerSend {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border: none;
  border-radius: var(--radius-md);
  background: var(--bg-active);
  color: var(--text-primary);
  cursor: pointer;
}

.composerSend:hover:not(:disabled) {
  background: var(--accent-primary);
}

.composerSend:disabled {
  opacity: 0.35;
  cursor: default;
}

/* Stop replaces Send while a turn is in flight — same footprint, so the
   composer doesn't reflow at the moment you might be reaching for it. */
.composerStop {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border: none;
  border-radius: var(--radius-md);
  background: var(--ai-gradient);
  color: var(--text-primary);
  cursor: pointer;
}

.composerStop:hover {
  background: var(--ai-gradient-flip);
}

/* ── Canvas ──────────────────────────────────────────────── */

.canvas {
  min-width: 0;
  overflow-y: auto;
  padding: var(--space-4) 0 var(--space-10) var(--space-6);
}

.canvasScope {
  margin: 0 0 var(--space-5);
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.scopeSubject {
  font-weight: var(--weight-semi);
  color: var(--text-secondary);
}

.scopeSep {
  padding: 0 4px;
  color: var(--text-disabled);
}

/* Twelfths, so full/half/third all divide cleanly and a row never leaves a
   ragged gap. Blocks still flow top to bottom — this adds width control, not
   free-form placement. */
.blocks {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  align-items: start;
  gap: var(--space-4);
  max-width: 1100px;
}

.block[data-layout='full'] {
  grid-column: span 12;
}

.block[data-layout='half'] {
  grid-column: span 6;
}

.block[data-layout='third'] {
  grid-column: span 4;
}

/* Below ~900px the panes stack and there isn't room for columns; everything
   goes full width rather than squeezing a chart into a third of a phone. */
@media (max-width: 900px) {
  .block[data-layout='half'],
  .block[data-layout='third'] {
    grid-column: span 12;
  }
}

/* A block is a document section, not a draggable widget.
   The enter animation runs on mount only — blocks are keyed by a stable id, so
   an agent's new block animates in while the ones already on the canvas sit
   still. That difference is the point: it shows *what changed*. */
.block {
  padding: var(--space-5);
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  animation: blockIn var(--duration-base) var(--ease-default) both;
}

@keyframes blockIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .block {
    animation: none;
  }
}

.block:hover {
  border-color: var(--border-default);
}

.blockHeader {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
  margin-bottom: var(--space-4);
}

/* ── Banner: the deliverable's cover, so it runs full-bleed ─────────────── */

.block[data-type='banner'] {
  position: relative;
  padding: 0;
  overflow: hidden;
  border-color: transparent;
}

/* Header collapses to a floating action bar — no title row to lay out, so it
   must not reserve vertical space above the artwork. */
.block[data-type='banner'] .blockHeader {
  position: absolute;
  top: var(--space-3);
  right: var(--space-3);
  margin: 0;
  z-index: 3;
}

.blockTitle {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--text-lg);
  font-weight: var(--weight-bold);
  color: var(--text-primary);
  line-height: 1.25;
}

.blockSubtitle {
  margin: var(--space-1) 0 0 0;
  font-size: var(--text-sm);
  color: var(--text-muted);
}

/* Actions stay hidden until the block is hovered — selection scopes intent */
.blockActions {
  display: flex;
  gap: var(--space-2);
  opacity: 0;
  transition: opacity var(--duration-fast) var(--ease-default);
}

.block:hover .blockActions,
.block:focus-within .blockActions {
  opacity: 1;
}

.blockAction {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  height: 26px;
  padding: 0 var(--space-2);
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: var(--weight-semi);
  color: var(--text-secondary);
  cursor: pointer;
}

/* ── Reordering ───────────────────────────────────────────── */

.dragHandle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: var(--radius-sm);
  color: var(--text-muted);
  cursor: grab;
}

.dragHandle:hover {
  color: var(--text-primary);
  background: var(--bg-active);
}

.dragHandle:active {
  cursor: grabbing;
}

.dragHandle:focus-visible {
  outline: 2px solid var(--ai-accent);
  outline-offset: 2px;
}

.block[data-dragging] {
  opacity: 0.4;
}

/* The insertion point, drawn as a leading edge rather than a full outline —
   it reads as "it lands here" instead of "this block is selected". */
.block[data-dropping] {
  box-shadow: -3px 0 0 0 var(--ai-accent);
}

/* ── Add prose ────────────────────────────────────────────── */

.addRow {
  grid-column: span 12;
  display: flex;
  gap: var(--space-2);
}

.addBtn {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  background: none;
  border: 1px dashed var(--border-default);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--text-muted);
  cursor: pointer;
  transition:
    color var(--duration-fast) var(--ease-default),
    border-color var(--duration-fast) var(--ease-default);
}

.addBtn:hover {
  color: var(--text-primary);
  border-color: var(--ai-accent);
}

.addBtn:focus-visible {
  outline: 2px solid var(--ai-accent);
  outline-offset: 2px;
}

.blockAction:hover {
  color: var(--text-primary);
  border-color: var(--accent-primary);
}

.blocksJson {
  max-width: 1100px;
  margin: 0;
  padding: var(--space-4);
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  font-family: var(--font-mono);
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  color: var(--text-secondary);
  overflow-x: auto;
  white-space: pre;
}

.canvasEmpty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-12) var(--space-6);
  text-align: center;
  background: var(--bg-card);
  border: 1px dashed var(--border-default);
  border-radius: var(--radius-lg);
  max-width: 1100px;
}

.canvasEmptyTitle {
  margin: 0;
  font-size: var(--text-lg);
  font-weight: var(--weight-semi);
  color: var(--text-primary);
}

.canvasEmptyText {
  margin: 0;
  font-size: var(--text-base);
  color: var(--text-muted);
}

.canvasEmptyBtn {
  height: 32px;
  padding: 0 var(--space-4);
  background: var(--bg-elevated);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: var(--weight-semi);
  color: var(--text-primary);
  cursor: pointer;
}

.canvasEmptyBtn:hover {
  border-color: var(--accent-primary);
}

@media (max-width: 900px) {
  .split {
    grid-template-columns: minmax(0, 1fr);
  }

  .chatPanel {
    border-right: none;
    border-bottom: 1px solid var(--border-subtle);
    padding-right: 0;
    max-height: 40vh;
  }

  .canvas {
    padding-left: 0;
  }
}





/* ── Awaiting a subject ───────────────────────────────────── */

/* The one control that needs attention gets it. A steady ring rather than a
   pulse — the canvas is a document, and a blinking control in a document reads
   as an error state rather than an invitation. */



.awaiting {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--ai-surface);
  border: 1px solid var(--ai-border);
  border-radius: var(--radius-lg);
}

.awaitingIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border-radius: var(--radius-pill);
  background: var(--ai-gradient);
  color: var(--text-primary);
}

.awaitingTitle {
  margin: 0;
  font-size: var(--text-base);
  font-weight: var(--weight-semi);
  color: var(--text-primary);
}

.awaitingText {
  margin: var(--space-1) 0 0 0;
  font-size: var(--text-sm);
  color: var(--text-muted);
  line-height: var(--leading-normal);
}

/* ══════════════════════════════════════════════════════════
   PRINT — the deliverable, not the editor

   Everything that exists to *build* the document is removed: chat, scope
   controls, hover actions, tabs, the app shell. What remains is the block
   grid, laid out to the page. exportPdf() forces the light theme first, so
   these rules assume paper.
   ══════════════════════════════════════════════════════════ */

@media print {
  .header,
  .chatPanel,
  .canvasScope,
  .blockActions,
  .addRow,
  .awaiting {
    display: none !important;
  }

  .page,
  .split,
  .canvas {
    display: block !important;
    height: auto !important;
    overflow: visible !important;
    padding: 0 !important;
    background: #fff !important;
  }

  .blocks {
    max-width: none;
    gap: 16px;
  }

  /* A block that straddles a page break is unreadable in a PDF, and a heading
     stranded at the foot of a page is worse. */
  .block {
    break-inside: avoid;
    page-break-inside: avoid;
    border-color: #ddd !important;
    box-shadow: none !important;
    background: #fff !important;
  }

  .block[data-type='text'] {
    break-inside: auto;
    page-break-inside: auto;
  }

  /* Recharts sizes to its container, which has no layout during print unless
     it keeps an explicit height. */
  .block svg {
    max-width: 100%;
  }
}

@page {
  margin: 14mm;
}

/* ══════════════════════════════════════════════════════════
   PRESENT — the document, without the editor
   ══════════════════════════════════════════════════════════ */

.presentWrap {
  position: relative;
}

.presentBtn {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: 30px;
  padding: 0 var(--space-3);
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: var(--weight-semi);
  color: var(--text-secondary);
  cursor: pointer;
}

.presentBtn:hover {
  color: var(--text-primary);
  border-color: var(--border-strong);
}

/* Sits under the menu and over everything else, so any outside click lands
   here and closes it — no document-level listener to leak. */
.menuScrim {
  position: fixed;
  inset: 0;
  z-index: 40;
}

.menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 41;
  min-width: 170px;
  padding: var(--space-1);
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
}

.menuItem {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-2) var(--space-3);
  background: none;
  border: none;
  border-radius: var(--radius-sm);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--text-secondary);
  text-align: left;
  cursor: pointer;
}

.menuItem:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

/* ── Reading mode ─────────────────────────────────────────── */

/* The only thing that floats. Opaque so it never lets artwork bleed through,
   and small enough that the document is otherwise unobstructed. */
.presentExit {
  position: fixed;
  top: var(--space-4);
  right: var(--space-5);
  z-index: 60;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-pill);
  box-shadow: var(--shadow-lg);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--text-secondary);
  cursor: pointer;
}

.presentExit:hover {
  color: var(--text-primary);
  border-color: var(--ai-accent);
}

.presentExit:focus-visible {
  outline: 2px solid var(--ai-accent);
  outline-offset: 2px;
}

/* Document title, in flow — the same header the export writes, so Present
   shows the reader what the reader will actually get. */
.docHead {
  max-width: 1100px;
  margin: 0 auto var(--space-6);
  padding-bottom: var(--space-4);
  border-bottom: 1px solid var(--border-default);
}

.docTitle {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--text-2xl);
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-tight);
  color: var(--text-primary);
  line-height: 1.2;
}

.docScope {
  margin: var(--space-2) 0 0;
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.kbd {
  padding: 1px 5px;
  border-radius: var(--radius-sm);
  background: var(--bg-active);
  font-family: var(--font-mono, monospace);
  font-size: var(--text-xs);
  color: var(--text-muted);
}

/* Strip the editor. Everything hidden here exists to *build* the document. */
.page[data-presenting] .header,
.page[data-presenting] .chatPanel,
.page[data-presenting] .canvasScope,
.page[data-presenting] .blockActions,
.page[data-presenting] .addRow {
  display: none !important;
}

.page[data-presenting] .split {
  display: block;
}

/* Nothing fixed spans the top any more, so the document just needs normal
   breathing room — no clearance to keep in step with. */
.page[data-presenting] .canvas {
  padding: var(--space-8) var(--space-6) var(--space-10);
  overflow-y: auto;
}

.page[data-presenting] .blocks {
  margin: 0 auto;
}

/* No hover lift or drop-shadow shifting under the reader's cursor. */
.page[data-presenting] .block:hover {
  border-color: var(--border-subtle);
}

/* ── Composing (iterative turns) ──────────────────────────── */

/* A claimed slot for the block that is arriving. Dashed rather than solid so
   it reads as "reserved" instead of "here is a block you can act on". */
.composing {
  position: relative;
  grid-column: span 12;
  padding: var(--space-5);
  border: 1px dashed var(--ai-border);
  border-radius: var(--radius-lg);
  background: var(--bg-card);
  animation: composeIn var(--duration-base) var(--ease-default) both;
}

.composingDot {
  position: absolute;
  top: var(--space-4);
  right: var(--space-4);
  width: 8px;
  height: 8px;
  border-radius: var(--radius-pill);
  background: var(--ai-accent);
  animation: breathe 1.6s var(--ease-default) infinite;
}

@keyframes composeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes breathe {
  0%,
  100% {
    opacity: 0.35;
    transform: scale(0.85);
  }
  50% {
    opacity: 1;
    transform: scale(1.15);
  }
}

@media (prefers-reduced-motion: reduce) {
  .composing,
  .composingDot {
    animation: none;
  }
}
